import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Currencies,
  FeatureFlagTypes,
  Frequency,
  type WorkflowCompletion,
} from '@generated/graphql';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CountryCodes } from '@onevesthq/ov-enums';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { AccountTypeSelect } from '@ovComponents/3-pattern/accountTypeSelect/accountTypeSelect';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { BankAccountSelectField } from '@ovComponents/3-pattern/bankAccountSelectField/bankAccountSelect';
import { BankSelectField } from '@ovComponents/3-pattern/bankSelectField/bankSelect';
import { HoldingSelectField } from '@ovComponents/3-pattern/holdingSelectField/holdingSelect';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { ISO_DATE_FORMAT } from '@ovComponents/4-module/workflowCompletion/constants/dateFormats';
import {
  DEPOSIT_TRANSFER_TYPE,
  DepositStarting,
  PARTICIPANT_NUMBER_LENGTH,
  TRANSFER_TYPES_WITHOUT_SCHEDULE,
} from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import { FISCAL_YEAR_SELECT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import {
  type ValidateTransferResponse,
  validateTransfer,
} from '@ovComponents/accountRestrictions';
import { TransferErrorBanner } from '@ovComponents/transferErrorBanner';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { UserContext } from '@providers/userContextProvider';
import { isBusinessDayAndAllowWeekend } from '@utils/businessDays';
import { DEFAULT_SUPPORT_URL } from '@utils/constants';
import { currencySymbol, getCurrencyFlag } from '@utils/currency';
import { FlagsmithFlags } from '@utils/flagsmith';
import { formatMoneyValue } from '@utils/moneyFormatting';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Stack,
  Typography,
  useTheme,
  useToast,
} from 'hephaestus/web';
import { Info as InfoIcon } from 'hephaestus/webIcons';
import { isNull, isUndefined } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import {
  FETCH_SUBACCOUNT_CURRENCY_STATS,
  FETCH_TOTAL_TRANSFERS,
  FETCH_USER_LOCALIZATION,
} from '../queries';
import { invalidFields } from '../utils';
import {
  getDepositErrorBannerKey,
  isEftFormEnabled,
  isFhsaTransferFormEnabled,
  isFhsaTransferRuleApplicable,
  isInternalTransferFormEnabled,
} from '../utils/depositAccountRestrictions';
import {
  getDepositValidationError,
  toAmountCents,
} from '../utils/depositAmountRules';
import {
  type CurrencyBalance,
  getAvailableCurrenciesForTransfer,
  hasInsufficientCurrencyBalance,
  isMultiCurrencyInternalTransfer,
  sharesCurrencyWithSelection,
  shouldClearCounterpartSubAccount,
} from '../utils/depositCurrencies';
import {
  buildDepositFieldOptions,
  isParticipantNumberLengthValid,
} from '../utils/depositFieldOptions';
import {
  depositAccountConfig,
  showFiscalYearBasedOnAccountType,
} from '../utils/depositFiscalYear';
import {
  checkTransferDelay,
  isScheduledDateEditable,
  isValidTransferDelayCheck,
  resolveDefaultScheduleOption,
  resolveScheduledDateError,
  resolveScheduledDateErrorMessage,
  resolveScheduledDateMinDate,
  shouldClearScheduledDate,
  shouldDefaultToFutureDate,
  type TransferDelayCheck,
} from '../utils/depositSchedule';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import {
  internalTransferSourceFilter,
  withdrawSubAccountFilter,
} from '../withdrawForm/withdrawSubAccountFilter';

export const DepositForm = ({
  options,
  onNext,
  dummyDeposit,
  setDummyDeposit,
  workflowCompletion,
}: {
  options: any;
  onNext: any;
  dummyDeposit?: any;
  setDummyDeposit?: any;
  stepLoading?: boolean;
  workflowCompletion?: WorkflowCompletion;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();
  const { userId: paramsUserId } = useParams();
  const canWriteOverrideTransferRestrictions = usePermission(
    'write:override_transfer_restrictions',
  );
  const canWriteTransferOboClients = usePermission(
    'write:transfer_obo_clients',
  );
  const canWriteTransferBasic = usePermission('write:transfer_basic');
  const { enqueueToast } = useToast();
  const { activeEntity, activeOrganization, userContext, custodianConnection } =
    useContext(UserContext);

  const {
    applicableData,
    updateContextWithFields,
    saveTemporaryDeposit,
    mode,
  } = useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);

  // Seed `applicableData.type` from `options.defaultTransferType` on entry
  // when DEPOSIT_TYPE is not in the workflow. The `applicableData?.type`
  // guard makes this a no-op when DEPOSIT_TYPE precedes the form (type
  // already set) or when editing an existing deposit (type hydrated from
  // the deposit).
  useEffect(() => {
    if (mode === 'edit') return;
    if (applicableData?.type) return;
    const fallback = options?.defaultTransferType;
    if (!fallback) return;
    updateContextWithFields({ type: fallback });
  }, []);

  const [focused, setFocused] = useState<string[]>([]);
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const defaultToFutureDate = shouldDefaultToFutureDate({
    applicableData,
    options,
  });

  const [starting, setStarting] = useState<DepositStarting>(
    applicableData.schedule === Frequency.OneTime && defaultToFutureDate
      ? DepositStarting.FutureDate
      : DepositStarting.Now,
  );
  useEffect(() => {
    if (defaultToFutureDate) {
      setStarting(DepositStarting.FutureDate);
    } else {
      setStarting(DepositStarting.Now);
    }
  }, [defaultToFutureDate]);

  const [totalTransfers, setTotalTransfers] = useState<number | null>(null);
  const [dateHasChanged, setDateHasChanged] = useState(false);
  const [validateErrorAmounts, setValidateErrorAmounts] = useState({
    hasError: false,
    errorMessage: '',
  });
  const [scheduleError, setScheduleError] = useState<string>('');
  const [currencyBalance, setCurrencyBalance] =
    useState<CurrencyBalance | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Prefer sub-account owner over activeEntity when the route has no :userId (household nav).
  const userIdFromSubAccount = applicableData?.subAccount?.account?.user?.id;
  const userId = paramsUserId ?? userIdFromSubAccount ?? activeEntity?.id;
  const subAccountFilter = withdrawSubAccountFilter({
    contextObjectType: applicableData?.objectType,
    contextObjectId: applicableData?.objectId,
    completionObjectType: workflowCompletion?.objectType,
    completionObjectId: workflowCompletion?.objectId,
    userId,
  });
  // Skip fetchBankAccounts until :userId or sub-account when we would otherwise use only activeEntity.
  const bankAccountsQueryUserId = paramsUserId
    ? (userId ?? '')
    : applicableData?.subAccount?.id
      ? (userId ?? '')
      : '';

  const overrideTransferRestrictions = canWriteOverrideTransferRestrictions;
  const { data: localizationData } = useQuery(FETCH_USER_LOCALIZATION, {
    variables: { userId },
    skip: !userId,
  });
  const usTransferFlowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.UsTransferFlow,
  );
  const [fetchTotal] = useLazyQuery(FETCH_TOTAL_TRANSFERS);

  const enableInKind = useOrganizationFeatureFlag(
    FeatureFlagTypes.InKindTransfers,
  );
  const enabledManualInstitutionCreation = useOrganizationFeatureFlag(
    FeatureFlagTypes.AddInstitutionsManuallyInUce,
  );
  const enableMultiCurrencyDepositTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyDepositTransfers,
  );
  const isMultiCurrencyInternal = isMultiCurrencyInternalTransfer({
    transferType: applicableData?.type,
    enableMultiCurrencyDepositTransfers,
  });
  const availableCurrencies = getAvailableCurrenciesForTransfer({
    applicableData,
  });
  const weekdaysOnlyCalendar = useFeatureFlag(
    FlagsmithFlags.weekdays_only_calendar,
  );
  const { data: subAccountData } = useQuery(FETCH_SUBACCOUNT_CURRENCY_STATS, {
    variables: {
      subAccountId: applicableData?.fromSubAccount?.id,
      currency: applicableData?.currency,
    },
    skip:
      !isMultiCurrencyInternal ||
      !applicableData?.fromSubAccount?.id ||
      !applicableData?.currency,
  });

  useEffect(() => {
    if (subAccountData?.fetchSubAccount?.subAccount?.statistics) {
      const stats = subAccountData.fetchSubAccount.subAccount.statistics;
      const availableCents = stats.availableFundsToWithdrawCents || 0;

      setCurrencyBalance({
        availableCents,
        currency: applicableData?.currency || '',
      });
    } else if (applicableData?.currency) {
      setCurrencyBalance(null);
    }
  }, [
    subAccountData,
    applicableData?.currency,
    applicableData?.fromSubAccount?.id,
  ]);

  const scheduledDateForConfig = applicableData?.scheduledDate;
  const applicableAccountType = applicableData?.subAccount?.account
    ?.type as keyof ReturnType<typeof depositAccountConfig>;

  const depositConfig = depositAccountConfig(scheduledDateForConfig)[
    applicableAccountType
  ];
  const isCurrentContributionYearEnabled = depositConfig
    ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
    : false;

  const generateContributionYearMenuItems = () => {
    const currentScheduleDate = applicableData?.scheduledDate;
    if (
      applicableData.schedule === Frequency.OneTime &&
      starting === DepositStarting.Now
    ) {
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs().isBefore(depositConfig.endOfPriorFiscalYear) ||
          dayjs().isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.LAST_YEAR}
            value={currentYear - 1}
          >
            {currentYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentYear}
          >
            {currentYear}
          </MenuItem>
        ),
      ];
    }
    if (
      applicableData.schedule === Frequency.OneTime &&
      starting === DepositStarting.FutureDate
    ) {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isBefore(
            depositConfig.endOfPriorFiscalYear,
          ) ||
          dayjs(currentScheduleDate).isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.LAST_YEAR}
            value={currentSelectedYear - 1}
          >
            {currentSelectedYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }
    if (applicableData.schedule !== Frequency.OneTime) {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      return [
        currentSelectedYear && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }

    return [];
  };

  const hyperLinkColor = theme.color.negativeOutline;
  const supportUrl =
    translateBackendString(userContext.organization?.translatedSupportUrl) ||
    DEFAULT_SUPPORT_URL;
  const currentYear = dayjs().year();

  const transferDate = applicableData?.scheduledDate;
  const delayDays = options?.transferDelay?.number;
  const transferCountry =
    applicableData?.bankAccount?.bankAccountTransitPhysicalAddress?.country ||
    localizationData?.fetchUser?.user?.physicalAddress?.country ||
    localizationData?.fetchUser?.user?.countryOfTaxResidence ||
    activeOrganization.applicableLocalization.countries[0];

  const participantNumberEnabled: boolean =
    usTransferFlowEnabled &&
    (applicableData.subAccount?.account?.type?.includes('USA_') ?? false);

  const clearValidateErrorAmountsState = () =>
    setValidateErrorAmounts({ hasError: false, errorMessage: '' });

  const handleTotalTransfers = async (subAccount: { id: string }) => {
    const { data } = await fetchTotal({
      variables: { input: { filter: { subAccountId: subAccount.id } } },
    });
    setTotalTransfers(data.fetchTransfers?.totalCount);
    checkValidationErrors(data.fetchTransfers?.totalCount);
  };

  const isInitiatedByRepresentative = () =>
    canWriteTransferOboClients && !canWriteTransferBasic;

  const [transferDelayCheck, setTransferDelayCheck] =
    useState<TransferDelayCheck | null>(null);
  const [scheduledDateIsBusinessDay, setScheduledDateIsBusinessDay] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [delayResult, isBd] = await Promise.all([
        checkTransferDelay({
          scheduledDate: transferDate,
          days: delayDays,
          country: transferCountry,
        }),
        isBusinessDayAndAllowWeekend({
          date: dayjs(transferDate).utc().toDate(),
          country: transferCountry,
        }),
      ]);
      if (!cancelled) {
        setTransferDelayCheck(delayResult);
        setScheduledDateIsBusinessDay(isBd);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transferDate, delayDays, transferCountry]);

  const isEft = applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT;
  const isScheduled = starting !== DepositStarting.Now;
  const validTransferDelayCheck = isValidTransferDelayCheck({
    isEft,
    isScheduled,
    options,
    transferDelayCheck,
    scheduledDateIsBusinessDay,
  });

  const scheduledDateError = resolveScheduledDateError({
    transferDate,
    options,
    transferDelayCheck,
    scheduledDateIsBusinessDay,
  });

  const scheduledDateErrorMessage = () => {
    const { key, params } = resolveScheduledDateErrorMessage({
      options,
      transferDelayCheck,
    });
    return t(key, params);
  };

  const checkValidationErrors = (transfersCount: number | null) => {
    clearValidateErrorAmountsState();
    setScheduleError('');

    const validationError = getDepositValidationError({
      applicableData,
      options,
      transfersCount,
      organization: activeOrganization,
      t,
    });

    if (validationError?.field === 'amountCents') {
      setValidateErrorAmounts({
        hasError: true,
        errorMessage: validationError.message,
      });
    }
    if (validationError?.field === 'schedule') {
      setScheduleError(validationError.message);
    }
  };

  useEffect(() => {
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
  }, [applicableData?.amountCents, applicableData?.schedule]);

  useEffect(() => {
    const update = async () => {
      if (applicableData?.subAccount?.id) {
        await handleTotalTransfers({
          id: applicableData.subAccount.id,
          ...applicableData.subAccount,
        });
      }
    };
    update();
  }, [applicableData?.subAccount]);

  const defaultScheduleOption = resolveDefaultScheduleOption(options);
  const isNotComingFromSubAccount =
    options?.subAccount?.enabled &&
    applicableData.objectType !== PageObjectType.SUB_ACCOUNT;
  const disabled = disableForm;

  const handleValue = (value: Partial<typeof applicableData>) =>
    updateContextWithFields(value);

  const handleDateChange = async (date: any) => {
    const formattedDate = dayjs(date?.toString()).format(ISO_DATE_FORMAT);
    setDateHasChanged(true);
    const update = { scheduledDate: formattedDate } as Partial<
      typeof applicableData
    >;

    const newDelayCheck = await checkTransferDelay({
      scheduledDate: dayjs(date).toDate(),
      days: delayDays,
      country: transferCountry,
    });

    if (shouldClearScheduledDate({ date, options, newDelayCheck })) {
      update.scheduledDate = undefined;
    }
    await handleValue(update);
  };

  useEffect(() => {
    if (applicableData?.schedule !== Frequency.OneTime) {
      handleValue({
        fiscalYear: undefined,
        schedule: applicableData?.schedule ?? defaultScheduleOption,
      });
    }
    if (
      applicableData.schedule === Frequency.OneTime &&
      starting === DepositStarting.Now
    ) {
      handleValue({ scheduledDate: undefined });
    }
    const type = applicableData?.type;
    if (type && TRANSFER_TYPES_WITHOUT_SCHEDULE.includes(type)) {
      handleValue({
        scheduledDate: undefined,
        schedule: Frequency.OneTime,
      });
    }
    if (isCurrentContributionYearEnabled) {
      handleValue({ fiscalYear: currentYear });
    }
  }, [applicableData?.schedule, starting, options]);

  const getOriginOfAccount = (
    accountType?: string,
  ): CountryCodes | undefined => {
    if (!accountType || accountType === '') return undefined;
    if (accountType.includes('USA_')) return CountryCodes.US;
    if (accountType.includes('UK_')) return CountryCodes.GB;
    return CountryCodes.CA;
  };

  const originAccountType = getOriginOfAccount(
    applicableData.subAccount?.account?.type,
  );

  useEffect(() => {
    if (!isFhsaTransferRuleApplicable({ applicableData })) return;
    const isFormEnabled = isFhsaTransferFormEnabled({
      applicableData,
      overrideTransferRestrictions,
    });
    setDisableForm(!isFormEnabled);
  }, [applicableData]);

  useEffect(() => {
    if (
      !isMultiCurrencyInternal ||
      !applicableData?.fromSubAccount?.id ||
      !applicableData?.subAccount?.id
    ) {
      return;
    }

    const availableCurrencies = getAvailableCurrenciesForTransfer({
      applicableData,
    });

    if (
      availableCurrencies.length === 1 &&
      availableCurrencies[0] !== applicableData?.currency
    ) {
      handleValue({ currency: availableCurrencies[0] });
    }
  }, [
    isMultiCurrencyInternal,
    applicableData?.fromSubAccount?.id,
    applicableData?.subAccount?.id,
  ]);

  const submit = async () => {
    setIsSubmitted(true);

    // If no deposit type is selected, allow user to proceed without saving data
    if (!applicableData?.type) {
      enqueueToast(t('noDepositTypeSelectedWarning'), {
        variant: 'warning',
      });
      await onNext();
      return;
    }

    const fields = invalidFields(
      buildDepositFieldOptions({
        options,
        applicableData,
        starting,
        participantNumberEnabled,
        isMultiCurrencyInternalTransfer: isMultiCurrencyInternal,
        hasAvailableCurrencies: availableCurrencies.length > 0,
      }),
      applicableData,
    );

    if (isNull(totalTransfers) && applicableData?.subAccount?.id) {
      await handleTotalTransfers({
        id: applicableData.subAccount.id,
        ...applicableData.subAccount,
      });
    }
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
    if (validateErrorAmounts.hasError) {
      fields.push('amountCents');
    }

    if (
      participantNumberEnabled &&
      applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER
    ) {
      const participantNumberValue = applicableData?.participantNumber ?? '';
      if (!isParticipantNumberLengthValid(participantNumberValue)) {
        fields.push('participantNumber');
      }
    }

    if (
      currencyBalance &&
      hasInsufficientCurrencyBalance({
        isMultiCurrencyInternalTransfer: isMultiCurrencyInternal,
        applicableData,
        currencyBalance,
      })
    ) {
      enqueueToast(
        t('components:transferModal.insufficientCurrencyBalance', {
          currency: currencyBalance.currency,
          available: formatMoneyValue(
            currencyBalance.availableCents,
            currencySymbol[
              currencyBalance.currency as keyof typeof currencySymbol
            ] || '$',
          ),
        }),
        { variant: 'error' },
      );
      fields.push('amountCents');
    }

    if (fields.length > 0 || !validTransferDelayCheck) {
      setFocused([...fields]);
      return;
    }
    const transferValidation: ValidateTransferResponse = validateTransfer({
      accountTo: applicableData?.subAccount?.account?.type ?? '',
      transferType:
        applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT
          ? 'electronicFundsTransferIn'
          : applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER
            ? 'externalTransfer'
            : 'internalTransferTo',
      accountFrom: applicableData?.fromSubAccount?.account?.type,
      canOverrideTransferRestrictions: canWriteOverrideTransferRestrictions,
    });

    if (!transferValidation.isValid) {
      enqueueToast(transferValidation.message, { variant: 'error' });
      return;
    }
    if (fields.length === 0) {
      await saveTemporaryDeposit();
      await onNext();
    }
  };

  const handleEftValidation = (e: any) => {
    const isFormEnabled = isEftFormEnabled({
      selectedSubAccount: e,
      applicableData,
    });
    setDisableForm(!isFormEnabled);
  };

  const handleInternalTransferFromValidation = (e: any) => {
    const isFormEnabled = isInternalTransferFormEnabled({
      selectedSubAccount: e,
      counterpartSubAccount: applicableData?.subAccount,
      applicableData,
    });
    setDisableForm(!isFormEnabled);
  };

  const handleInternalTransferToValidation = (e: any) => {
    const isFormEnabled = isInternalTransferFormEnabled({
      selectedSubAccount: e,
      counterpartSubAccount: applicableData?.fromSubAccount,
      applicableData,
    });
    setDisableForm(!isFormEnabled);
  };

  const getErrorBanner = () => {
    const i18nKey = getDepositErrorBannerKey({
      applicableData,
      overrideTransferRestrictions,
    });

    if (!i18nKey) return null;

    return (
      <TransferErrorBanner
        showHeader={false}
        theme={theme}
        i18nKey={i18nKey}
        supportUrl={supportUrl}
        linkSx={{ color: hyperLinkColor }}
      />
    );
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled,
        dataTestId: 'deposit-form-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' component='div' sx={{ mb: 3 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget={'_blank'}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}
      {!applicableData?.type && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {t('noDepositTypeSelected')}
        </Alert>
      )}
      {applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT && (
        <>
          {enableMultiCurrencyDepositTransfers ? (
            <>
              {options?.subAccount?.enabled &&
                applicableData.objectType !== PageObjectType.SUB_ACCOUNT && (
                  <SubAccountSelect
                    omitAny
                    sx={{ mb: 2 }}
                    filter={subAccountFilter}
                    dataTestId='deposit-form-subaccount-select'
                    label={translateBackendString(options?.subAccount?.label)}
                    onSubAccountSelect={async (e: any) => {
                      handleEftValidation(e);
                      handleValue({ subAccount: e, bankAccount: undefined });
                      await handleTotalTransfers(e);
                    }}
                    selectedSubAccount={applicableData?.subAccount}
                    onBlur={() => setFocused([...focused, 'subAccount'])}
                    error={
                      !applicableData?.subAccount?.id &&
                      focused.includes('subAccount') &&
                      options?.subAccount?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                    showCurrencyAvatar={true}
                    displayCustodianAccountNumber={
                      options.displayCustodianAccountNumber
                    }
                    selectFirstSubAccountOnly={
                      options.selectFirstSubAccountOnly
                    }
                  />
                )}
              {options?.bankAccount?.enabled && (
                <BankAccountSelectField
                  key={`bank-account-select-${applicableData?.subAccount?.id}`}
                  userId={bankAccountsQueryUserId}
                  sx={{ mb: 2 }}
                  disabled={!bankAccountsQueryUserId}
                  setBankAccount={(e: any) => handleValue({ bankAccount: e })}
                  bankAccount={applicableData?.bankAccount || null}
                  account={applicableData?.subAccount?.account}
                  onBlur={() => setFocused([...focused, 'bankAccount'])}
                  error={
                    !applicableData?.bankAccount?.id &&
                    focused.includes('bankAccount') &&
                    options?.bankAccount?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  options={options}
                  showSelectedIcon={true}
                />
              )}
            </>
          ) : (
            <>
              {options?.subAccount?.enabled &&
                applicableData?.objectType !== PageObjectType.SUB_ACCOUNT && (
                  <SubAccountSelect
                    omitAny
                    sx={{ mb: 2 }}
                    filter={subAccountFilter}
                    dataTestId='deposit-form-subaccount-select'
                    label={translateBackendString(options?.subAccount?.label)}
                    onSubAccountSelect={async (e: any) => {
                      handleEftValidation(e);
                      handleValue({ subAccount: e, bankAccount: undefined });
                      await handleTotalTransfers(e);
                    }}
                    selectedSubAccount={applicableData?.subAccount}
                    onBlur={() => setFocused([...focused, 'subAccount'])}
                    error={
                      !applicableData?.subAccount?.id &&
                      focused.includes('subAccount') &&
                      options?.subAccount?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                    displayCustodianAccountNumber={
                      options.displayCustodianAccountNumber
                    }
                    selectFirstSubAccountOnly={
                      options.selectFirstSubAccountOnly
                    }
                  />
                )}
              {options?.bankAccount?.enabled && (
                <BankAccountSelectField
                  key={`bank-account-select-${applicableData?.subAccount?.id}`}
                  userId={bankAccountsQueryUserId}
                  sx={{ mb: 2 }}
                  disabled={!bankAccountsQueryUserId}
                  setBankAccount={(e: any) => handleValue({ bankAccount: e })}
                  bankAccount={applicableData?.bankAccount || null}
                  account={applicableData?.subAccount?.account}
                  onBlur={() => setFocused([...focused, 'bankAccount'])}
                  error={
                    !applicableData?.bankAccount?.id &&
                    focused.includes('bankAccount') &&
                    options?.bankAccount?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  options={options}
                  showSelectedIcon={!!enableMultiCurrencyDepositTransfers}
                />
              )}
            </>
          )}
          {options?.holdings?.enabled && (
            <HoldingSelectField
              userId={userId ?? ''}
              sx={{ mb: 2 }}
              disabled={!applicableData?.subAccount?.account?.id}
              label={translateBackendString(options?.holdings?.label)}
              setAccountHolding={(e: any) => handleValue({ holdings: e })}
              onBlur={() => setFocused([...focused, 'holdings'])}
              selectedSubAccount={applicableData?.subAccount}
              selectedHolding={applicableData?.holdings}
              error={
                !applicableData?.holdings?.financialProduct?.cusip &&
                options?.holdings?.required !== FieldRequiredType.NOT_REQUIRED
              }
              isSubmitted={isSubmitted}
              options={options}
              holdingFilterOptions={{
                includeCash: custodianConnection?.allowCashAsDepositTarget,
              }}
            />
          )}
          {options?.fiscalYear?.enabled &&
            showFiscalYearBasedOnAccountType(
              applicableAccountType as string,
            ) && (
              <SelectField
                fullWidth
                sx={{ mb: 2 }}
                testId={FISCAL_YEAR_SELECT_TEST_ID.SELECT}
                value={applicableData?.fiscalYear}
                label={translateBackendString(options?.fiscalYear?.label)}
                onChange={(e: any) =>
                  handleValue({ fiscalYear: e.target.value })
                }
              >
                {generateContributionYearMenuItems()}
              </SelectField>
            )}
          {options?.amountCents?.enabled && (
            <AmountField
              disabled={!applicableData?.subAccount || totalTransfers === null}
              useFormatAmountValueCents
              sx={{ mb: 2 }}
              testId='deposit-form-amount-cents'
              label={translateBackendString(options?.amountCents?.label)}
              setAmount={(e: any) =>
                handleValue({
                  amountCents: toAmountCents(e),
                })
              }
              amount={applicableData?.amountCents}
              onBlur={() => setFocused([...focused, 'amountCents'])}
              error={
                (!applicableData?.amountCents &&
                  focused.includes('amountCents') &&
                  options?.amountCents?.required !==
                    FieldRequiredType.NOT_REQUIRED) ||
                (validateErrorAmounts.hasError &&
                  focused.includes('amountCents'))
              }
              errorText={
                validateErrorAmounts.hasError
                  ? validateErrorAmounts.errorMessage
                  : undefined
              }
              {...(enableMultiCurrencyDepositTransfers
                ? {
                    currency:
                      applicableData?.bankAccount?.currency ||
                      applicableData?.subAccount?.account?.baseCurrency ||
                      activeOrganization?.productShelfCurrencies?.[0] ||
                      Currencies.Cad,
                  }
                : {})}
            />
          )}
          {options?.schedule?.enabled && (
            <SelectField
              fullWidth
              sx={{ mb: 2 }}
              testId='schedule-select'
              value={applicableData?.schedule ?? defaultScheduleOption}
              label={translateBackendString(options?.schedule?.label)}
              onChange={(e: any) => handleValue({ schedule: e.target.value })}
              onBlur={() => setFocused([...focused, 'schedule'])}
              error={
                !applicableData?.schedule &&
                focused.includes('schedule') &&
                options?.schedule?.required !== FieldRequiredType.NOT_REQUIRED
              }
              errorText={scheduleError}
            >
              <MenuItem
                data-testid='schedule-select-one-time'
                value={Frequency.OneTime}
              >
                {t('schedule.ONE_TIME')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-weekly'
                value={Frequency.Weekly}
              >
                {t('schedule.WEEKLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-bi-weekly'
                value={Frequency.BiWeekly}
              >
                {t('schedule.BI_WEEKLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-semi-monthly'
                value={Frequency.SemiMonthly}
              >
                {t('schedule.SEMI_MONTHLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-monthly'
                value={Frequency.Monthly}
              >
                {t('schedule.MONTHLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-quarterly'
                value={Frequency.Quarterly}
              >
                {t('schedule.QUARTERLY')}
              </MenuItem>
            </SelectField>
          )}
          {options?.scheduledDate?.enabled &&
            applicableData?.schedule !== Frequency.OneTime && (
              <DateField
                fullWidth
                sx={{ mb: 2 }}
                dataTestId='scheduled-date'
                onChange={handleDateChange}
                label={translateBackendString(options?.scheduledDate?.label)}
                minDate={resolveScheduledDateMinDate({
                  options,
                  transferDelayCheck,
                })}
                shouldDisableDate={(date: any) =>
                  !isScheduledDateEditable({
                    date,
                    schedule: applicableData?.schedule,
                    weekdaysOnlyCalendar,
                    transferCountry,
                  })
                }
                value={applicableData?.scheduledDate}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage()}
                autocomplete='off'
              />
            )}
          {(options?.allowOneTimeTransfersFutureDate ||
            options?.allowOneTimeTransfersNow) &&
            applicableData?.schedule === Frequency.OneTime && (
              <RadioGroup
                testId='deposit-form-starting'
                label={t('components:transferModal.starting')}
                sx={{ pb: 0 }}
                value={starting}
                onChange={(e: any) => {
                  setStarting(e.target.value);
                  if (e.target.value === DepositStarting.FutureDate) {
                    setFocused([...focused, 'scheduledDate']);
                  }
                  if (e.target.value === DepositStarting.Now) {
                    handleValue({ scheduledDate: undefined });
                  }
                }}
              >
                <Stack direction={'row'}>
                  {!isInitiatedByRepresentative() &&
                    options?.allowOneTimeTransfersNow && (
                      <Radio
                        testId='deposit-form-starting-now'
                        value={DepositStarting.Now}
                        label={t(
                          'components:transferModal.startingOptions.now',
                        )}
                      />
                    )}
                  {options?.allowOneTimeTransfersFutureDate && (
                    <Radio
                      testId='deposit-form-starting-future'
                      value={DepositStarting.FutureDate}
                      label={t(
                        'components:transferModal.startingOptions.futureDate',
                      )}
                    />
                  )}
                </Stack>
              </RadioGroup>
            )}
          {starting === DepositStarting.FutureDate &&
            applicableData?.schedule === Frequency.OneTime && (
              <DateField
                fullWidth
                sx={{ mb: 2 }}
                dataTestId='scheduled-date'
                onChange={handleDateChange}
                label={translateBackendString(options?.scheduledDate?.label)}
                minDate={resolveScheduledDateMinDate({
                  options,
                  transferDelayCheck,
                })}
                shouldDisableDate={(date: any) =>
                  !isScheduledDateEditable({
                    date,
                    schedule: applicableData?.schedule,
                    weekdaysOnlyCalendar,
                    transferCountry,
                  })
                }
                value={applicableData?.scheduledDate}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage()}
                autocomplete='off'
              />
            )}
        </>
      )}

      {options?.transferDelay?.enabled &&
        ((applicableData.schedule === Frequency.OneTime &&
          starting !== DepositStarting.Now) ||
          applicableData.schedule !== Frequency.OneTime) && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            {translateBackendString(options?.transferDelayDescription)}
          </Alert>
        )}

      {applicableData?.type === DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER && (
        <>
          {options?.fromSubAccount?.enabled && (
            <SubAccountSelect
              omitAny
              sx={{ mb: 2 }}
              userId={userId}
              filter={internalTransferSourceFilter({
                contextObjectType: applicableData?.objectType,
                contextObjectId: applicableData?.objectId,
                completionObjectType: workflowCompletion?.objectType,
                completionObjectId: workflowCompletion?.objectId,
                userId,
              })}
              dataTestId='deposit-form-from-sub-account-select'
              label={translateBackendString(options?.fromSubAccount?.label)}
              // if this page is navigated from the subAccount page. The subAccount its coming from should have its
              // id assigned to the subAccountsToExclude, so it is not shown in the list of subAccount from, since it is
              // the subAccount money would be transferred into subAccountTo
              subAccountsToExclude={
                !isNotComingFromSubAccount && applicableData?.subAccount?.id
                  ? [applicableData?.subAccount?.id]
                  : []
              }
              filterSubAccount={(subAccount: SubAccount) =>
                sharesCurrencyWithSelection({
                  subAccount,
                  selectedSubAccount: applicableData?.subAccount,
                  enableMultiCurrencyDepositTransfers,
                })
              }
              onSubAccountSelect={(e: any) => {
                handleInternalTransferFromValidation(e);
                const shouldClearTo = shouldClearCounterpartSubAccount({
                  selectedSubAccount: e,
                  counterpartSubAccount: applicableData?.subAccount,
                  enableMultiCurrencyDepositTransfers,
                });

                if (shouldClearTo) {
                  handleValue({
                    subAccount: undefined,
                    fromSubAccount: e,
                    currency: undefined,
                  });
                } else {
                  handleValue({ fromSubAccount: e, currency: undefined });
                }
              }}
              selectedSubAccount={applicableData?.fromSubAccount}
              onBlur={() => setFocused([...focused, 'fromSubAccount'])}
              error={
                !applicableData?.fromSubAccount &&
                focused.includes('fromSubAccount') &&
                options?.fromSubAccount?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
              showCurrencyAvatar={!!enableMultiCurrencyDepositTransfers}
              displayCustodianAccountNumber={
                options.displayCustodianAccountNumber
              }
              selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
            />
          )}
          {isNotComingFromSubAccount && (
            <SubAccountSelect
              omitAny
              filter={subAccountFilter}
              sx={{ mb: 2 }}
              dataTestId='deposit-form-sub-account-select'
              label={translateBackendString(options?.subAccount?.label)}
              subAccountsToExclude={
                applicableData?.fromSubAccount?.id
                  ? [applicableData?.fromSubAccount?.id]
                  : []
              }
              filterSubAccount={(subAccount: SubAccount) =>
                sharesCurrencyWithSelection({
                  subAccount,
                  selectedSubAccount: applicableData?.fromSubAccount,
                  enableMultiCurrencyDepositTransfers,
                })
              }
              onSubAccountSelect={(e: any) => {
                handleInternalTransferToValidation(e);
                const shouldClearFrom = shouldClearCounterpartSubAccount({
                  selectedSubAccount: e,
                  counterpartSubAccount: applicableData?.fromSubAccount,
                  enableMultiCurrencyDepositTransfers,
                });

                if (shouldClearFrom) {
                  handleValue({
                    fromSubAccount: undefined,
                    subAccount: e,
                    currency: undefined,
                  });
                } else {
                  handleValue({ subAccount: e, currency: undefined });
                }
              }}
              selectedSubAccount={applicableData?.subAccount}
              onBlur={() => setFocused([...focused, 'subAccount'])}
              error={
                (!applicableData?.subAccount ||
                  Object.keys(applicableData.subAccount).length === 0) &&
                focused.includes('subAccount') &&
                options?.subAccount?.required !== FieldRequiredType.NOT_REQUIRED
              }
              showCurrencyAvatar={!!enableMultiCurrencyDepositTransfers}
              displayCustodianAccountNumber={
                options.displayCustodianAccountNumber
              }
              selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
            />
          )}
          {enableMultiCurrencyDepositTransfers &&
            applicableData?.fromSubAccount?.id &&
            applicableData?.subAccount?.id &&
            availableCurrencies.length > 1 && (
              <SelectField
                fullWidth
                sx={{ mb: 2 }}
                testId='internal-transfer-currency-select'
                label={t('transfer:selectCurrency')}
                value={applicableData?.currency || ''}
                onChange={(e: any) => {
                  handleValue({ currency: e.target.value });
                  setFocused([...focused, 'currency']);
                }}
                error={
                  !applicableData?.currency &&
                  focused.includes('currency') &&
                  availableCurrencies.length > 0
                }
              >
                {availableCurrencies.map((currency) => (
                  <MenuItem
                    key={currency}
                    value={currency}
                    data-testid={`currency-option-${currency}`}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <img
                        src={getCurrencyFlag(currency)}
                        alt={currency}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                      {currency}
                    </Box>
                  </MenuItem>
                ))}
              </SelectField>
            )}
          {options?.amountCents?.enabled && (
            <>
              <AmountField
                useFormatAmountValueCents
                sx={{ mb: 1 }}
                testId='deposit-form-amount-cents'
                label={translateBackendString(options?.amountCents?.label)}
                setAmount={(e: any) =>
                  handleValue({
                    amountCents: toAmountCents(e),
                  })
                }
                amount={applicableData?.amountCents}
                onBlur={() => setFocused([...focused, 'amountCents'])}
                error={
                  (!applicableData?.amountCents &&
                    focused.includes('amountCents') &&
                    options?.amountCents?.required !==
                      FieldRequiredType.NOT_REQUIRED) ||
                  (validateErrorAmounts.hasError &&
                    focused.includes('amountCents'))
                }
                errorText={
                  validateErrorAmounts.hasError
                    ? validateErrorAmounts.errorMessage
                    : undefined
                }
                {...(enableMultiCurrencyDepositTransfers &&
                applicableData?.currency
                  ? { currency: applicableData.currency }
                  : {})}
              />
              {enableMultiCurrencyDepositTransfers &&
                applicableData?.currency &&
                currencyBalance && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 2,
                      mt: 0.5,
                    }}
                    data-testid='currency-balance-display'
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color:
                          (applicableData?.amountCents || 0) >
                          currencyBalance.availableCents
                            ? 'error.main'
                            : 'info.main',
                      }}
                    />
                    <Typography variant='labelLarge' color='textSecondary'>
                      {t('transfer:availableCurrencyBalance', {
                        currency: currencyBalance.currency,
                      })}
                    </Typography>
                    <Typography
                      variant='bodyLarge'
                      color={
                        (applicableData?.amountCents || 0) >
                        currencyBalance.availableCents
                          ? 'error'
                          : 'textPrimary'
                      }
                      sx={{ fontWeight: 600 }}
                    >
                      {formatMoneyValue(
                        currencyBalance.availableCents,
                        currencySymbol[
                          currencyBalance.currency as keyof typeof currencySymbol
                        ] || '$',
                      )}
                    </Typography>
                  </Box>
                )}
            </>
          )}
          {!enableMultiCurrencyDepositTransfers &&
            (applicableData?.fromSubAccount?.statistics?.marketValueCents ??
              0) > 0 &&
            (applicableData?.amountCents || 0) >
              (applicableData?.fromSubAccount?.statistics?.marketValueCents ||
                0) *
                0.97 && (
              <Typography
                variant='labelSmall'
                component='div'
                color='error'
                sx={{ mt: 1 }}
              >
                {t('depositReview.amountExceeds')}
              </Typography>
            )}

          {showFiscalYearBasedOnAccountType(
            applicableAccountType as string,
          ) && (
            <SelectField
              fullWidth
              sx={{ mb: 2 }}
              testId={FISCAL_YEAR_SELECT_TEST_ID.SELECT}
              value={applicableData?.fiscalYear}
              label={translateBackendString(options?.fiscalYear?.label)}
              onChange={(e: any) => handleValue({ fiscalYear: e.target.value })}
            >
              {generateContributionYearMenuItems()}
            </SelectField>
          )}
        </>
      )}

      {applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER && (
        <>
          {options?.subAccount?.enabled &&
            applicableData.objectType !== PageObjectType.SUB_ACCOUNT && (
              <SubAccountSelect
                omitAny
                filter={subAccountFilter}
                sx={{ mb: 2 }}
                dataTestId='deposit-form-sub-account-select'
                label={translateBackendString(options?.subAccount?.label)}
                onSubAccountSelect={(e: any) =>
                  handleValue({ subAccount: e, accountType: undefined })
                }
                selectedSubAccount={applicableData?.subAccount}
                onBlur={() => setFocused([...focused, 'subAccount'])}
                error={
                  (!applicableData?.subAccount ||
                    Object.keys(applicableData?.subAccount).length === 0) &&
                  focused.includes('subAccount') &&
                  options?.subAccount?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
                displayCustodianAccountNumber={
                  options.displayCustodianAccountNumber
                }
                selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
              />
            )}
          {options?.institution?.enabled && (
            <BankSelectField
              fullWidth
              sx={{ mb: 2 }}
              label={translateBackendString(options?.institution?.label)}
              setInstitution={(e: any) => handleValue({ institution: e })}
              institution={applicableData?.institution}
              onBlur={() => setFocused([...focused, 'institution'])}
              error={
                (!applicableData?.institution ||
                  Object.keys(applicableData.institution).length === 0) &&
                focused.includes('institution') &&
                options?.institution?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
              addInstitutionManually={enabledManualInstitutionCreation}
              originOfAccount={
                originAccountType && usTransferFlowEnabled
                  ? originAccountType
                  : undefined
              }
            />
          )}
          {participantNumberEnabled &&
            (() => {
              const participantNumberValue =
                applicableData?.participantNumber ?? '';
              const participantNumberTouched =
                focused.includes('participantNumber');
              const participantNumberMissing =
                participantNumberTouched && participantNumberValue.length === 0;
              const participantNumberInvalidLength =
                participantNumberTouched &&
                !isParticipantNumberLengthValid(participantNumberValue);

              return (
                <TextField
                  testId='participant-number-for-us-transfers'
                  infoTooltip={t('transfer:participantNumberTooltip')}
                  onChange={(e: any) => {
                    const digitsOnly = e.target.value
                      .replace(/\D/g, '')
                      .slice(0, PARTICIPANT_NUMBER_LENGTH);
                    handleValue({ participantNumber: digitsOnly });
                  }}
                  label={t('transfer:participantNumber')}
                  fullWidth
                  sx={{ mt: 2, mb: 2 }}
                  value={participantNumberValue}
                  inputProps={{
                    maxLength: PARTICIPANT_NUMBER_LENGTH,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  onBlur={() => setFocused([...focused, 'participantNumber'])}
                  error={
                    participantNumberMissing || participantNumberInvalidLength
                  }
                  errorText={
                    participantNumberInvalidLength
                      ? t('transfer:participantNumberLengthError')
                      : undefined
                  }
                />
              );
            })()}
          {options?.accountNumber?.enabled && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              testId='deposit-form-account-number'
              label={translateBackendString(options?.accountNumber?.label)}
              onChange={(e: any) =>
                handleValue({ accountNumber: e.target.value })
              }
              value={applicableData?.accountNumber}
              onBlur={() => setFocused([...focused, 'accountNumber'])}
              error={
                !applicableData?.accountNumber &&
                focused.includes('accountNumber') &&
                options?.accountNumber?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          )}
          {options?.accountType?.enabled &&
            (!usTransferFlowEnabled || originAccountType) && (
              <AccountTypeSelect
                size='medium'
                label={translateBackendString(options?.accountType?.label)}
                onChange={(e: any) => handleValue({ accountType: e })}
                value={applicableData?.accountType}
                onBlur={() => setFocused([...focused, 'institution'])}
                error={
                  !applicableData?.accountType &&
                  focused.includes('accountType') &&
                  options?.accountType?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
                originOfAccount={
                  originAccountType && usTransferFlowEnabled
                    ? originAccountType
                    : undefined
                }
                bypassFilteringByAvailableFeatureFlags={
                  originAccountType && usTransferFlowEnabled
                }
              />
            )}
          {options?.transferType?.enabled && !usTransferFlowEnabled && (
            <SelectField
              fullWidth
              sx={{ mb: 2, mt: 2 }}
              testId='transfer-type-select'
              label={translateBackendString(options?.transferType?.label)}
              onChange={(e: any) =>
                handleValue({ transferType: e.target.value })
              }
              value={applicableData?.transferType}
              onBlur={() => setFocused([...focused, 'transferType'])}
              error={
                !applicableData?.transferType &&
                focused.includes('transferType') &&
                options?.transferType?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem
                data-testid='transfer-type-select-all-cash'
                value='ALL_IN_CASH'
              >
                {t('depositReview.ALL_IN_CASH')}
              </MenuItem>
              <MenuItem
                data-testid='transfer-type-select-partial-cash'
                value='PARTIAL_CASH'
              >
                {t('depositReview.PARTIAL_CASH')}
              </MenuItem>
              {enableInKind && (
                <MenuItem
                  data-testid='transfer-type-select-all-in-kind'
                  value='ALL_IN_KIND'
                >
                  {t('depositReview.ALL_IN_KIND')}
                </MenuItem>
              )}
            </SelectField>
          )}
          {options?.transferType?.enabled && usTransferFlowEnabled && (
            <SelectField
              fullWidth
              sx={{ mb: 2, mt: 2 }}
              testId='transfer-type-select'
              label={translateBackendString(options?.transferType?.label)}
              onChange={(e: any) =>
                handleValue({ transferType: e.target.value })
              }
              value={applicableData?.transferType}
              onBlur={() => setFocused([...focused, 'transferType'])}
              error={
                !applicableData?.transferType &&
                focused.includes('transferType') &&
                options?.transferType?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              {activeOrganization?.enableFullCashTransfers && (
                <MenuItem
                  data-testid='transfer-type-select-all-cash'
                  value='ALL_IN_CASH'
                >
                  {t('depositReview.ALL_IN_CASH')}
                </MenuItem>
              )}
              {activeOrganization?.enablePartialCashTransfers && (
                <MenuItem
                  data-testid='transfer-type-select-partial-cash'
                  value='PARTIAL_CASH'
                >
                  {t('depositReview.PARTIAL_CASH')}
                </MenuItem>
              )}
              {activeOrganization?.enableAllInKindTransfers && enableInKind && (
                <MenuItem
                  data-testid='transfer-type-select-all-in-kind'
                  value='ALL_IN_KIND'
                >
                  {t('depositReview.ALL_IN_KIND')}
                </MenuItem>
              )}
            </SelectField>
          )}
          {options?.amountCents?.enabled && (
            <AmountField
              useFormatAmountValueCents
              testId='deposit-form-amount-cents'
              label={
                applicableData?.transferType === 'ALL_IN_CASH'
                  ? t('depositReview.estimatedAmountLabel')
                  : translateBackendString(options?.amountCents?.label)
              }
              setAmount={(e: any) =>
                handleValue({
                  amountCents: toAmountCents(e),
                })
              }
              amount={applicableData?.amountCents}
              onBlur={() => setFocused([...focused, 'amountCents'])}
              error={
                (!applicableData?.amountCents &&
                  focused.includes('amountCents') &&
                  options?.amountCents?.required !==
                    FieldRequiredType.NOT_REQUIRED) ||
                (validateErrorAmounts.hasError &&
                  focused.includes('amountCents'))
              }
              errorText={
                validateErrorAmounts.hasError
                  ? validateErrorAmounts.errorMessage
                  : undefined
              }
            />
          )}
          {(applicableData?.fromSubAccount?.statistics?.marketValueCents ?? 0) >
            0 &&
            (applicableData?.amountCents || 0) >
              (applicableData?.fromSubAccount?.statistics?.marketValueCents ||
                0) *
                0.97 && (
              <Typography
                variant='labelSmall'
                component='div'
                color='error'
                sx={{ mt: 1 }}
              >
                {t('depositReview.amountExceeds')}
              </Typography>
            )}
        </>
      )}

      {applicableData?.schedule !== Frequency.OneTime &&
        showFiscalYearBasedOnAccountType(
          applicableData?.subAccount?.account?.type,
        ) && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            <Typography variant='bodySmall' component='p'>
              {t('fiscalYearForRecurringContributions')}
            </Typography>
          </Alert>
        )}

      {/* Later this section will be implement as editable from configuration level */}
      {applicableData?.transferType &&
        applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER && (
          <Box sx={{ mt: 2, mb: 2 }}>
            {applicableData?.transferType === 'ALL_IN_CASH' && (
              <Alert variant='outlined' icon={<InfoIcon />} severity='info'>
                <Typography variant='bodySmall' component='p'>
                  {t('depositReview.allInCashMessage')}
                </Typography>
              </Alert>
            )}
            {applicableData?.transferType === 'PARTIAL_CASH' && (
              <Alert variant='outlined' icon={<InfoIcon />} severity='info'>
                <Typography variant='bodySmall' component='p'>
                  {t('depositReview.partialCashMessage')}
                </Typography>
              </Alert>
            )}
            {applicableData?.transferType === 'ALL_IN_KIND' && (
              <Alert variant='outlined' icon={<InfoIcon />} severity='info'>
                <Typography variant='bodySmall' component='p'>
                  {t('depositReview.allInKindMessage')}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

      {getErrorBanner()}
    </WorkflowCompletionForm>
  );
};

export default DepositForm;
